// assets/vm-glow.js (v6 theme-hover, 9 Oct 2026; owner "hover heat yes"). Hover heat: the heated zones glow on
// the photo being shown, always with the label "Illustrated heated zones".
// - Only photos listed in snippets/vm-glow-map.liquid have a glow. The layer is the theme asset
//   vm-glow-<media id>.webp (transparent, same aspect as the photo), named in the host's data-vm-glow.
// - Nothing loads on page load: cards import this module on the first mouse hover or keyboard focus
//   (assets/vm-heat-hover.js); the layer and assets/vm-glow.css (data-vm-glow-css) load on first use.
// - Product gallery: <vm-glow> (snippets/vm-gallery-caption.liquid) sits in each listed photo. Desktop: hover
//   or keyboard focus on that photo. Phones and touch screens: a "Show heat" button (aria-pressed) on the
//   photo; it stays on for the whole gallery while the shopper swipes (the photo on screen glows).
// - Fade in about 0.8 s, one slow breathe, then steady; leaving fades out. prefers-reduced-motion: instant.
//   The collection heat view (html[data-vm-view="heat"], vm-view-switch.js) wins over any glow.
const RM = matchMedia('(prefers-reduced-motion: reduce)');
const TOUCH = matchMedia('(hover: none), (max-width: 749px)');
const R = document.documentElement;
const EASE = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
const S = new WeakMap();
let css;

/** assets/vm-glow.css, once per page. Nothing is drawn before it has loaded (no unstyled flash, no shift). */
function sheet(url) {
  return (css ||= new Promise((ok, no) => {
    if (!url) return no();
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = url;
    l.onload = ok;
    l.onerror = no;
    document.head.append(l);
  }));
}

/** Per-host state; a host whose layer URL changed (Horizon morph re-using the element) starts over. */
function st(h) {
  let s = S.get(h);
  if (!s || s.url !== h.dataset.vmGlow) {
    s?.img?.remove();
    S.set(h, (s = { url: h.dataset.vmGlow, src: new Set() }));
  }
  return s;
}

function ready(h, s) {
  if (!s.ready) {
    const img = (s.img = document.createElement('img'));
    img.className = 'vm-glow__img';
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.decoding = 'async';
    if (h.dataset.vmBlend) img.dataset.blend = h.dataset.vmBlend;
    const got = new Promise((ok, no) => {
      img.onload = ok;
      img.onerror = no;
    });
    img.src = s.url;
    // decode off the main thread when the browser can, but never wait on it for more than 150 ms
    s.ready = Promise.all([got, sheet(h.dataset.vmGlowCss)]).then(() =>
      Promise.race([img.decode ? img.decode().catch(() => {}) : 0, new Promise((r) => setTimeout(r, 150))])
    );
  }
  return s.ready.then(() => {
    if (!s.img.isConnected) {
      const photo = h.querySelector(':scope > .vm-tile__img');
      if (photo) photo.after(s.img);
      else h.prepend(s.img);
    }
    if (!h.querySelector(':scope > .vm-glow-pill')) {
      const p = document.createElement('span');
      p.className = 'vm-glow-pill';
      p.setAttribute('aria-hidden', 'true');
      p.textContent = 'Illustrated heated zones';
      s.img.after(p);
    }
  });
}

function fade(g, on, instant) {
  const from = parseFloat(getComputedStyle(g).opacity) || 0;
  g.getAnimations?.().forEach((a) => a.cancel());
  g.style.opacity = on ? '1' : '0';
  if (instant || RM.matches || !g.animate || from === (on ? 1 : 0)) return;
  if (on) {
    // ~0.8 s fade in, then one slow, gentle breathe, then rest at full
    g.animate(
      [
        { opacity: from, easing: EASE },
        { opacity: 1, offset: 0.29, easing: 'ease-in-out' },
        { opacity: 0.8, offset: 0.64, easing: 'ease-in-out' },
        { opacity: 1 },
      ],
      { duration: 2800 }
    );
  } else g.animate([{ opacity: from }, { opacity: 0 }], { duration: 450, easing: EASE });
}

/**
 * One reason for host h to glow ('hover', 'focus', 'toggle') goes on or off. h = <vm-card-tile> or <vm-glow>.
 * @param {HTMLElement} h
 * @param {string} why
 * @param {boolean} on
 * @param {boolean} [instant]
 */
export function glow(h, why, on, instant) {
  if (!h.dataset.vmGlow) return;
  const s = st(h);
  if (on) s.src.add(why);
  else s.src.delete(why);
  const hot = s.src.size > 0 && R.dataset.vmView !== 'heat' && !s.bad;
  if (hot === !!s.hot) return;
  s.hot = hot;
  if (!hot) {
    h.removeAttribute('data-vm-hot');
    if (s.img) fade(s.img, false);
    return;
  }
  ready(h, s).then(
    () => {
      if (!s.hot) return;
      h.setAttribute('data-vm-hot', '');
      fade(s.img, true, instant);
    },
    () => {
      s.bad = true;
      s.hot = false;
    }
  );
}

/** <vm-glow>: one listed photo in the product gallery (grid cell on desktop, slide on phones). */
class VmGlow extends HTMLElement {
  connectedCallback() {
    this.ac?.abort();
    const ac = (this.ac = new AbortController());
    const o = { signal: ac.signal };
    const cell = this.closest('slideshow-slide, li') || this.parentElement;
    this.gal = this.closest('media-gallery') || document.body;
    this.slide = this.closest('slideshow-slide');
    cell.addEventListener('pointerenter', (e) => e.pointerType !== 'touch' && glow(this, 'hover', true), o);
    cell.addEventListener('pointerleave', () => glow(this, 'hover', false), o);
    cell.addEventListener('focusin', (e) => {
      if (e.target !== this.btn && e.target.matches?.(':focus-visible')) glow(this, 'focus', true);
    }, o);
    cell.addEventListener('focusout', (e) => cell.contains(e.relatedTarget) || glow(this, 'focus', false), o);
    this.gal.addEventListener('vm-glow:sync', () => this.sync(), o);
    this.gal.addEventListener('slideshow:select', (e) => {
      if (this.slide && e.target.contains(this.slide)) this.sync(e.detail?.slide);
    }, o);
    // The button (phones, touch screens, narrow windows) needs the stylesheet first; desktop waits for first use.
    const build = () => sheet(this.dataset.vmGlowCss).then(() => ac.signal.aborted || this.build(), () => {});
    if (TOUCH.matches) build();
    else TOUCH.addEventListener('change', build, { once: true, signal: ac.signal });
  }

  disconnectedCallback() {
    this.ac?.abort();
    glow(this, 'hover', false);
    glow(this, 'focus', false);
  }

  build() {
    if (!this.btn?.isConnected) {
      const b = (this.btn = document.createElement('button'));
      b.type = 'button';
      b.className = 'vm-glow-btn';
      b.setAttribute('aria-pressed', 'false');
      b.innerHTML = '<span><i aria-hidden="true"></i>Show heat</span>';
      this.append(b);
    }
    this.sync();
  }

  /** The "Show heat" button was pressed (see the window click listener below). */
  toggle() {
    this.gal.toggleAttribute('data-vm-heat', this.btn?.getAttribute('aria-pressed') !== 'true');
    this.gal.dispatchEvent(new Event('vm-glow:sync'));
  }

  /**
   * Gallery heat on/off (data-vm-heat on <media-gallery>). Only a copy that is on screen glows: in the
   * slideshow the current slide; the desktop grid's copies are display:none on phones and the other way round.
   */
  sync(cur) {
    const on = this.gal.hasAttribute('data-vm-heat');
    let here = this.getClientRects().length > 0;
    if (this.slide) {
      const show = /** @type {any} */ (this.slide.closest('slideshow-component'));
      cur ||= show?.slides?.[show.current];
      here &&= cur ? cur === this.slide : this.slide.getAttribute('aria-hidden') !== 'true';
      if (this.btn) this.btn.tabIndex = here ? 0 : -1;
    }
    this.btn?.setAttribute('aria-pressed', String(on));
    glow(this, 'toggle', on && here);
  }
}

if (!customElements.get('vm-glow')) {
  customElements.define('vm-glow', VmGlow);
  // Window capture runs before Horizon's document-capture delegation, so the tap never reaches the photo's
  // on:click (the zoom dialog) or the slideshow; the button does its own job here.
  window.addEventListener(
    'click',
    (e) => {
      const b = /** @type {Element} */ (e.target)?.closest?.('.vm-glow-btn');
      const host = b?.closest('vm-glow');
      if (!host) return;
      e.preventDefault();
      e.stopPropagation();
      /** @type {any} */ (host).toggle?.();
    },
    true
  );
}
